import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import Answer from '../../answers/Answer'
import cn from 'classnames'
import styles from './post-embed.module.scss'
import { isMobile } from 'lib/device'
import { isEmpty } from '../../../lib/value'
import useOpenRight from 'refactor/hooks/useOpenRight'
import useOpenRightPrefetch from 'refactor/hooks/useOpenRightPrefetch'
import useCachedAnswer from 'refactor/hooks/api/useCachedAnswer'
import { queueAnswerHydration } from 'state/answerHydrationQueue'
import { compensateScrollForMeasurementChange, resolveReservedHeight } from 'lib/scrollStability'
import { mediaBucketsEnabled } from 'lib/mediaBuckets'
import { RowGeometryEventsProvider } from 'lib/rowGeometryEvents'
import { postEmbedAnswerWidth, resolvePostEmbedStaticLayout } from 'lib/answerPlaceholderHeight'
import ThinPostEmbed from './ThinPostEmbed'

// Stable no-op so the swallow-provider's memoized context value never churns per render.
const IGNORE_EMBED_ROW_GEOMETRY_EVENTS = () => {}
import { getAnswerPermalinkQuestKey } from 'utils/shareUtils'
import { useScrollResizeCompensation } from 'lib/scrollResizeCompensationContext'
import {
    refreshChildQuestInBackground,
    seedChildQuestFromAnswer,
} from 'refactor/panels/primeChildQuestForOpen'

const MAX_MEASURED_POST_EMBED_HEIGHTS = 1000
const measuredPostEmbedHeights = new Map()

const postEmbedHeightKey = (answerId, width = 0) =>
    `${answerId}:${Math.round(Number(width || 0) / 50)}`

const getMeasuredPostEmbedHeight = (answerId, width = 0) => {
    if (!answerId || answerId === '__PRIVATE__') return null

    if (Number(width || 0) > 0) {
        const exactHeight = measuredPostEmbedHeights.get(postEmbedHeightKey(answerId, width))
        if (exactHeight) return exactHeight
    }

    const prefix = `${answerId}:`
    let fallbackHeight = null
    for (const [key, height] of measuredPostEmbedHeights) {
        if (key.startsWith(prefix)) fallbackHeight = height
    }

    return fallbackHeight
}

const rememberMeasuredPostEmbedHeight = (answerId, height, width) => {
    if (!answerId || answerId === '__PRIVATE__') return
    if (!height || height <= 0) return
    if (Number(width || 0) <= 0) return

    measuredPostEmbedHeights.set(postEmbedHeightKey(answerId, width), height)
    if (measuredPostEmbedHeights.size <= MAX_MEASURED_POST_EMBED_HEIGHTS) return

    const oldestKey = measuredPostEmbedHeights.keys().next().value
    if (oldestKey) measuredPostEmbedHeights.delete(oldestKey)
}

const buildPermalinkAnswerTarget = (answer, fallbackAnswer) => ({
    ...(fallbackAnswer || {}),
    ...(answer || {}),
    child_quests: answer?.child_quests?.length ? answer.child_quests : fallbackAnswer?.child_quests,
    home_quest: answer?.home_quest || fallbackAnswer?.home_quest,
    home_quest_key: answer?.home_quest_key || fallbackAnswer?.home_quest_key,
    home_quest_id: answer?.home_quest_id || fallbackAnswer?.home_quest_id,
    uptree_quest_id: answer?.uptree_quest_id || fallbackAnswer?.uptree_quest_id,
    downtree_quest_id: answer?.downtree_quest_id || fallbackAnswer?.downtree_quest_id,
})

/**
 * @typedef {Object} PostEmbedProps
 * @property {any} answer
 * @property {any} [panel]
 * @property {any} [parentAnswer]
 * @property {any} [containerQuest]
 * @property {any} [containerAnswer]
 * @property {boolean} [editMode]
 * @property {(embedId: string) => void} [delete]
 * @property {boolean} [isTitle]
 * @property {boolean} [isQuote]
 * @property {string} [label]
 * @property {'childQuest' | 'permalink'} [openMode]
 * @property {(hover: boolean) => void} [onHeadEnter]
 * @property {(hover: boolean) => void} [onHeadLeave]
 * @property {(ev: React.MouseEvent) => void} [onMouseEnter]
 * @property {(ev: React.MouseEvent) => void} [onMouseLeave]
 * @property {() => void} [onContentLayoutSettled]
 * @property {number} [estimatedWidth]
 * @property {boolean} [staticGeometry]
 * @property {boolean} [thin] Render the one-row summary instead of the nested
 * answer. Same component, same data management — only the display changes.
 */

/**
 * @param {PostEmbedProps} props
 */
function PostEmbedInner(props) {
    const cachedAnswer = useCachedAnswer(props.answer?.id)
    const answer = props.answer?.id === '__PRIVATE__' ? props.answer : cachedAnswer || props.answer
    const answerId = answer?.id || props.answer?.id
    // A thin embed is one fixed row: nothing inside it hydrates taller, so it
    // never needs the clipped window (or the measurement) the full render does.
    const staticEmbedWindow =
        !props.thin &&
        !!props.staticGeometry &&
        mediaBucketsEnabled() &&
        Number(props.estimatedWidth || 0) > 0
    const staticLayout = useMemo(
        () =>
            staticEmbedWindow
                ? resolvePostEmbedStaticLayout({
                      answer,
                      estimatedWidth: props.estimatedWidth,
                  })
                : null,
        [answer, props.estimatedWidth, staticEmbedWindow],
    )
    const compensateScrollOnResize = useScrollResizeCompensation()
    const postContainerRef = useRef(null)
    // Whether the fixed window's content really overflows it. The projection's isClipped only
    // seeds the first paint — real text can wrap taller (or shorter) than estimated, and an
    // unmarked estimator miss leaves a hard mid-line slice at the window edge.
    const [measuredWindowOverflow, setMeasuredWindowOverflow] = useState(null)
    const initialReservedHeight = staticEmbedWindow
        ? staticLayout?.reservedHeight || 0
        : getMeasuredPostEmbedHeight(answerId) || 0
    const [reservedHeight, setReservedHeight] = useState(initialReservedHeight)
    const reservedHeightRef = useRef(initialReservedHeight)
    const lastMeasuredHeightRef = useRef(initialReservedHeight)
    const lastMeasuredTopRef = useRef(null)

    const { openRight } = useOpenRight()
    const opensPermalink = props.openMode === 'permalink'
    const prefetchQuestId = opensPermalink
        ? getAnswerPermalinkQuestKey(buildPermalinkAnswerTarget(answer, props.answer))
        : answer?.child_quests?.[0]?.id
    const prefetchHandlers = useOpenRightPrefetch(prefetchQuestId, answer?.id)

    useEffect(() => {
        if (!props.answer?.id) return
        if (props.answer.id === '__PRIVATE__') return
        queueAnswerHydration(props.answer.id, { lane: 'embed' })
    }, [props.answer?.id])

    useLayoutEffect(() => {
        setMeasuredWindowOverflow(null)
    }, [answerId, staticEmbedWindow])

    useLayoutEffect(() => {
        if (!staticEmbedWindow) return
        // Observe and measure the inner content clip box, not the window: the window's height
        // is pinned by the reserved inline style (so it never resizes or overflows itself).
        // Also observe the unclipped child: once the content box reaches its max-height, later
        // hydration changes only its scrollHeight and would not trigger its ResizeObserver.
        const content = postContainerRef.current?.firstElementChild
        if (!content) return
        const intrinsicContent = content.firstElementChild

        const measureOverflow = () => {
            setMeasuredWindowOverflow(content.scrollHeight > content.clientHeight + 1)
        }

        measureOverflow()

        if (typeof ResizeObserver === 'undefined') {
            const frameId = window.requestAnimationFrame(measureOverflow)
            return () => window.cancelAnimationFrame(frameId)
        }

        const observer = new ResizeObserver(measureOverflow)
        observer.observe(content)
        if (intrinsicContent) observer.observe(intrinsicContent)
        return () => observer.disconnect()
    }, [answerId, staticEmbedWindow])

    useLayoutEffect(() => {
        if (staticEmbedWindow) return
        const nextReservedHeight = getMeasuredPostEmbedHeight(answerId) || 0
        reservedHeightRef.current = nextReservedHeight
        lastMeasuredHeightRef.current = nextReservedHeight
        lastMeasuredTopRef.current = null
        setReservedHeight(nextReservedHeight)
    }, [answerId, staticEmbedWindow])

    const measureEmbedBox = useCallback(
        (root = postContainerRef.current) => {
            if (staticEmbedWindow) return null
            if (!root) return null

            const rect = root.getBoundingClientRect?.()
            const measuredHeight = rect?.height
            if (!measuredHeight || measuredHeight <= 0) return null

            const scrollContainer = root.closest?.('.scroll-container')
            const scrollRect = scrollContainer?.getBoundingClientRect?.()

            return {
                height: Math.ceil(measuredHeight),
                width: Math.ceil(rect?.width || 0),
                scrollContainer,
                top:
                    rect &&
                    scrollRect &&
                    Number.isFinite(rect.top) &&
                    Number.isFinite(scrollRect.top)
                        ? rect.top - scrollRect.top
                        : null,
            }
        },
        [staticEmbedWindow],
    )

    const setMeasuredHeight = useCallback(
        (nextMeasurement, { compensate = false, allowShrink = false } = {}) => {
            if (staticEmbedWindow) return
            const measuredHeight =
                typeof nextMeasurement === 'number' ? nextMeasurement : nextMeasurement?.height
            if (!measuredHeight || measuredHeight <= 0) return

            const previousHeight = lastMeasuredHeightRef.current || 0
            const nextHeight = resolveReservedHeight({
                measuredHeight,
                previousHeight,
                hasMeasuredHeight: previousHeight > 0,
                allowShrink,
            })
            if (!nextHeight) return
            const previousTop = lastMeasuredTopRef.current
            const nextTop = typeof nextMeasurement === 'number' ? null : nextMeasurement?.top
            const scrollContainer =
                typeof nextMeasurement === 'number' ? null : nextMeasurement?.scrollContainer
            const measuredWidth = typeof nextMeasurement === 'number' ? 0 : nextMeasurement?.width
            const { applied: appliedScrollCompensation, delta } =
                compensate && previousHeight
                    ? compensateScrollForMeasurementChange({
                          scrollContainer,
                          previousHeight,
                          previousTop,
                          nextHeight,
                          nextTop,
                      })
                    : { applied: false, delta: previousHeight ? nextHeight - previousHeight : 0 }

            lastMeasuredHeightRef.current = nextHeight
            lastMeasuredTopRef.current =
                Number.isFinite(nextTop) && appliedScrollCompensation ? nextTop - delta : nextTop
            rememberMeasuredPostEmbedHeight(answerId, nextHeight, measuredWidth)

            if (!reservedHeightRef.current) return

            reservedHeightRef.current = 0
            setReservedHeight(0)
        },
        [answerId, staticEmbedWindow],
    )

    const updateRenderedHeight = useCallback(
        options => {
            const nextMeasurement = measureEmbedBox()
            if (nextMeasurement != null) setMeasuredHeight(nextMeasurement, options)
        },
        [measureEmbedBox, setMeasuredHeight],
    )

    const handleChildContentLayoutSettled = useCallback(() => {
        if (staticEmbedWindow) return
        const notify = () => {
            updateRenderedHeight({ compensate: compensateScrollOnResize, allowShrink: true })
            props.onContentLayoutSettled?.()
        }

        if (typeof window.requestAnimationFrame !== 'function') {
            notify()
            return
        }

        window.requestAnimationFrame(notify)
    }, [
        compensateScrollOnResize,
        props.onContentLayoutSettled,
        staticEmbedWindow,
        updateRenderedHeight,
    ])

    useLayoutEffect(() => {
        if (staticEmbedWindow) return
        updateRenderedHeight({ compensate: compensateScrollOnResize })
    }, [answer, answerId, compensateScrollOnResize, staticEmbedWindow, updateRenderedHeight])

    useEffect(() => {
        if (staticEmbedWindow) return
        const node = postContainerRef.current
        if (!node) return

        if (typeof ResizeObserver === 'undefined') {
            const frameId = window.requestAnimationFrame(() =>
                updateRenderedHeight({ compensate: compensateScrollOnResize }),
            )
            return () => window.cancelAnimationFrame(frameId)
        }

        let frameId = null
        const observer = new ResizeObserver(() => {
            if (frameId != null) window.cancelAnimationFrame(frameId)
            frameId = window.requestAnimationFrame(() =>
                updateRenderedHeight({ compensate: compensateScrollOnResize }),
            )
        })
        observer.observe(node)
        updateRenderedHeight({ compensate: compensateScrollOnResize })

        return () => {
            if (frameId != null) window.cancelAnimationFrame(frameId)
            observer.disconnect()
        }
    }, [answerId, compensateScrollOnResize, staticEmbedWindow, updateRenderedHeight])

    const deleteEmbed = ev => {
        ev.preventDefault()
        ev.stopPropagation()

        props.delete(answer.id)
    }

    const onClick = async ev => {
        if (ev.target?.closest?.('.delete-embed-btn')) return
        // The artifact's ordinal-stream crumb owns its own destination; the
        // permalink capture handler must not hijack it.
        if (ev.target?.closest?.('[data-artifact-stream-crumb]')) return
        // Permalink embeds capture clicks before nested controls can stop propagation.
        // Let media controls consume their own activation instead of opening the post.
        if (ev.target?.closest?.('[data-answer-interactive="true"]')) return
        if (ev.target?.closest?.('[data-post-embed-navigation-exempt="true"]')) return

        ev.preventDefault()
        ev.stopPropagation()

        if (!answer || answer.id === '__PRIVATE__') return
        if (opensPermalink) {
            const questId = getAnswerPermalinkQuestKey(
                buildPermalinkAnswerTarget(answer, props.answer),
            )
            const targetAnswerId = answer.id || props.answer?.id
            if (questId && targetAnswerId) {
                await openRight({ questId, answerId: targetAnswerId })
            }
            return
        }

        const childQuestId = answer?.child_quests?.[0]?.id
        if (!childQuestId) return
        seedChildQuestFromAnswer(answer, childQuestId)
        await openRight({ questId: childQuestId, answerId: answer.id })
        refreshChildQuestInBackground(childQuestId)
    }

    const onMouseEnter = ev => {
        if (isMobile) return false
        // Here we do the opposite entry function bc entering this component is equivalent to leaving the parent comp.
        if (props.onMouseLeave) props.onMouseLeave(ev)
        if (props.onHeadLeave) props.onHeadLeave(ev)
    }

    const onMouseLeave = ev => {
        if (isMobile) return false
        // Here we do the opposite entry function bc entering this component is equivalent to leaving the parent comp.
        if (props.onMouseEnter) props.onMouseEnter(ev)
        if (props.onHeadEnter) props.onHeadEnter(ev)
    }

    let delta_json, isRecursiveQuote
    const hasDeltaJson = !isEmpty(answer?.delta_json)
    if (hasDeltaJson) {
        delta_json = answer.delta_json
    } else {
        isRecursiveQuote = true
        delta_json = answer?.nested_delta_json
    }

    const canDelete =
        gon.currentUser &&
        props.containerAnswer &&
        gon.currentUser.id === props.containerAnswer.user_id &&
        props.delete
    const showDelete = props.editMode && canDelete

    // Geometry-aware stream rows lock the embed to the same projected window reserved by the
    // parent row. Natural layouts keep the legacy measured min-height path.
    const reservedMinHeight = staticEmbedWindow ? staticLayout?.reservedHeight || 0 : reservedHeight

    return (
        <div
            className={cn(
                styles.embedComp,
                'embed-comp',
                staticEmbedWindow && styles.embedWindow,
                staticEmbedWindow &&
                    (measuredWindowOverflow ?? staticLayout?.isClipped) &&
                    styles.embedWindowClipped,
            )}
            data-testid="post-embed"
            data-embedded-answer-id={answerId}
            data-open-mode={props.openMode || 'childQuest'}
            onMouseOver={onMouseEnter}
            onMouseLeave={ev => {
                onMouseLeave(ev)
                prefetchHandlers.onMouseLeave()
            }}
            onMouseEnter={prefetchHandlers.onMouseEnter}
            onFocus={prefetchHandlers.onFocus}
            onBlur={prefetchHandlers.onBlur}
            onPointerDown={prefetchHandlers.onPointerDown}
            ref={postContainerRef}
            onClick={opensPermalink ? undefined : onClick}
            onClickCapture={opensPermalink ? onClick : undefined}
            style={
                reservedMinHeight
                    ? staticEmbedWindow
                        ? { height: reservedMinHeight }
                        : { minHeight: reservedMinHeight }
                    : undefined
            }
        >
            <div className={cn('parent-link', styles.windowContent)}>
                <div className="embed-main">
                    <div className="embed-header">
                        {false && props.label && (
                            <div className={cn(styles.parentLabel, 'parent-label pull-left')}>
                                {answer?.tweet && (
                                    <div className={styles.twitterLabel}>
                                        <i className="fa fa-twitter"></i>
                                    </div>
                                )}
                                {isRecursiveQuote && (
                                    <span className={cn(styles.recursiveLabel)}>...</span>
                                )}
                                {props.label}
                            </div>
                        )}

                        {showDelete && (
                            <div className="delete-embed-btn pull-right" onClick={deleteEmbed}>
                                <i className="fa fa-close" />
                            </div>
                        )}
                        <div style={{ clear: 'both' }} />
                    </div>

                    {props.thin ? (
                        <ThinPostEmbed answer={answer} fallbackAnswer={props.answer} />
                    ) : answer?.id === '__PRIVATE__' ? (
                        <div className={styles.privateEmbed}>
                            <i className="fa fa-lock" />
                        </div>
                    ) : (
                        // The nested answer carries its OWN id down to its tweet cards; letting
                        // those reach the host row's geometry provider would mark the WRONG row
                        // (the embedded answer's standalone card elsewhere in the list) as
                        // tweet-expanded. Embed content is window-clipped here, so expansion
                        // inside it never changes row geometry — swallow the events instead.
                        <RowGeometryEventsProvider
                            onTweetExpandedChange={IGNORE_EMBED_ROW_GEOMETRY_EVENTS}
                        >
                            <Answer
                                // @ts-expect-error - extra boolean props (isParent, isEmbed) not declared in Answer's class prop types
                                isParent
                                isEmbed
                                answer={answer}
                                showInsert={false}
                                showControls={false}
                                showTopControls={false}
                                showVotes={false}
                                selectable={false}
                                estimatedWidth={postEmbedAnswerWidth(props.estimatedWidth)}
                                inViewCallback={handleChildContentLayoutSettled}
                            />
                        </RowGeometryEventsProvider>
                    )}
                </div>
            </div>
        </div>
    )
}

const PostEmbed = React.memo(PostEmbedInner)

export default PostEmbed
